import type { LineChartProps } from '@shopify/polaris-viz';

import { useCampaignTemplateListing } from '~/modules/campaign-template';
import { EReportDataType, type IReportTable } from '../../common/type';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { AnalyticInterval } from '~/modules/analytics/types';
import { getFormattedByInterval } from '~/modules/analytics';
import { TIME_KEYS } from '~/modules/report-analytic';
import { formatReportData } from '@gem-sdk/analytics';
import { useMemo, useCallback } from 'react';

export interface UseChartDataProps {
  dimensions: string[];
  data: IReportTable;
  metricKey: string;
}

export const useChartData = ({ dimensions, data, metricKey }: UseChartDataProps) => {
  const { getTextPrice } = useConvertMoneyByCurrency();
  const { data: campaignTemplates } = useCampaignTemplateListing();

  const timeDimension = useMemo(() => dimensions.find((dim) => TIME_KEYS.has(dim)), [dimensions]);
  const interval = timeDimension?.toUpperCase() as AnalyticInterval;

  const templateLabelById = useMemo(() => {
    return campaignTemplates.reduce(
      (acc, template) => {
        acc[template.id] = template.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [campaignTemplates]);

  const formatter = useMemo(() => {
    return (
      data.columns.find((item) => item.name === metricKey)?.dataType || EReportDataType.INTEGER
    );
  }, [data.columns, metricKey]);

  const formatValue = useCallback(
    (value: number | string | null) => {
      return `${formatReportData({
        value,
        formatter,
        getTextPrice,
      })}`;
    },
    [getTextPrice, formatter],
  );

  const getIsExpandDetail = (interval: AnalyticInterval, isExpandDetail?: boolean) => {
    if (interval === AnalyticInterval.YEAR) return false;

    return isExpandDetail || interval !== AnalyticInterval.DAY;
  };

  const formatKeyByInterval = useCallback(
    (value: string | number | null, isExpandDetail?: boolean) => {
      return getFormattedByInterval(value, interval, {
        isExpandDetail: getIsExpandDetail(interval, isExpandDetail),
      });
    },
    [interval],
  );

  const seriesNameFormatter = (x: string | number | null) => {
    return `${x}`;
  };

  const yAxisOptions: LineChartProps['yAxisOptions'] = useMemo(() => {
    return {
      labelFormatter: formatValue,
    };
  }, [formatValue]);

  const onlyHasTimeDimension = dimensions.length === 1 && dimensions[0] === timeDimension;

  return {
    seriesNameFormatter,
    yAxisOptions,
    interval,
    timeDimension,
    formatValue,
    formatKeyByInterval,
    templateLabelById,
    onlyHasTimeDimension,
  };
};
